import { FunctionComponent, forwardRef } from 'react';
import { FONT_COLOURS } from '@belong/themes';
import { IReadOnlyRow } from './ReadOnlyRow.types';
import { RichText, Copy, TEXT_NODES } from '../../styles/Typography';
import * as ReadOnlyRowStyles from './ReadOnlyRow.styles';
import { PrimaryIcon, ValueIcon, PrimaryPicture } from './ReadOnlyRow.styles';
import { READ_ONLY_ROW_TEST_ID } from './ReadOnlyRow.constants';

// Quick abstraction component to tidy conditional rendering
const RowDescription = ({ value, ...props }) => {
  if (typeof value === 'string') {
    return <Copy {...props}>{value}</Copy>;
  }
  return value;
};

RowDescription.displayName = 'RowDescription';

export const ReadOnlyRow: FunctionComponent<React.PropsWithChildren<IReadOnlyRow>> = forwardRef<
  HTMLLIElement,
  IReadOnlyRow
>((props, ref) => {
  const { id, icon, valueIcon, image, isActive = true, hasColor = FONT_COLOURS.STRONG, ...copy } = props;
  const { title, heading, description, value, ...wrapperProps } = copy;
  const isTopAligned = wrapperProps.isTopAligned ?? (!!description && (!!title || !!heading));
  const textAlign = valueIcon ? 'left' : 'right';

  return (
    <ReadOnlyRowStyles.Container
      isTopAligned={isTopAligned}
      {...wrapperProps}
      data-testid={props['data-testid']}
      ref={ref}
    >
      {icon && <PrimaryIcon {...icon} isActive={isActive} />}
      {!icon && image && <PrimaryPicture {...image} />}
      <ReadOnlyRowStyles.Content isTopAligned={isTopAligned}>
        <ReadOnlyRowStyles.PrimaryContent>
          {title && typeof title === 'object' && (
            <RichText
              {...title}
              data-testid={READ_ONLY_ROW_TEST_ID.TITLE}
              hasMargin={{ xs: '0 0 0.8rem 0' }}
              hasColor={isActive ? FONT_COLOURS.STRONG : FONT_COLOURS.BASE}
              h3={TEXT_NODES.headingXSmall}
              h4={TEXT_NODES.headingXSmall}
              h5={TEXT_NODES.headingXSmall}
            />
          )}
          {!title && heading && typeof heading === 'string' && (
            <Copy data-testid={READ_ONLY_ROW_TEST_ID.HEADING} isBold hasColor={hasColor}>
              {heading}
            </Copy>
          )}
          {!title && heading && typeof heading === 'object' && <div>{heading}</div>}

          {description && (
            <ReadOnlyRowStyles.Description isTopAligned={isTopAligned}>
              <RowDescription
                value={description}
                data-testid={READ_ONLY_ROW_TEST_ID.DESCRIPTION}
                variant="small"
                hasColor={hasColor}
              />
            </ReadOnlyRowStyles.Description>
          )}
        </ReadOnlyRowStyles.PrimaryContent>
        {(value || valueIcon) && (
          <ReadOnlyRowStyles.Value>
            {valueIcon?.icon && <ValueIcon aria-label={valueIcon.iconLabel ?? ''} size="small" {...valueIcon.icon} />}
            {value && (
              <Copy data-testid={READ_ONLY_ROW_TEST_ID.VALUE} alignment={textAlign} hasColor={hasColor} isBold as="div">
                {value}
              </Copy>
            )}
          </ReadOnlyRowStyles.Value>
        )}
      </ReadOnlyRowStyles.Content>
    </ReadOnlyRowStyles.Container>
  );
});

ReadOnlyRow.displayName = 'ReadOnlyRow';
export default ReadOnlyRow;
